import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Briefcase, Car, CheckCircle, MailCheck, Phone, Shield, UserRound } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { sendConversionEvent } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const PHONE_REGEX = /^(\+?[1-9]\d{7,14}|0[2-478]\d{8}|04\d{8})$/;
const APPLY_SESSION_STORAGE_KEY = 'flash_apply_session_id';

const JOURNEY_OPTIONS = [
  {
    value: 'car_loan',
    title: 'Car Loan',
    description: 'Personal vehicle finance with guided support.',
    icon: Car,
  },
  {
    value: 'personal_loan',
    title: 'Personal Loan',
    description: 'Flexible personal lending for non-business needs.',
    icon: UserRound,
  },
  {
    value: 'business_finance',
    title: 'Business Finance',
    description: 'Funding built for business growth and cash flow.',
    icon: Briefcase,
  },
  {
    value: 'commercial_car_loan',
    title: 'Commercial Car Loan',
    description: 'Business vehicle finance with business lending criteria.',
    icon: Car,
  },
];

const BUSINESS_FUNDING_OPTIONS = [
  {
    value: 'asset_equipment_finance',
    title: 'Asset / Equipment Finance',
    applicationCategory: 'commercial_asset_finance',
    financeType: 'Asset / Equipment Finance',
  },
  {
    value: 'business_loan',
    title: 'Business Loan',
    applicationCategory: 'business_loans',
    financeType: 'Business Loan',
  },
  {
    value: 'cash_flow_finance',
    title: 'Cash Flow Finance',
    applicationCategory: 'business_loans',
    financeType: 'Cash Flow Finance',
  },
  {
    value: 'line_of_credit',
    title: 'Line of Credit',
    applicationCategory: 'business_loans',
    financeType: 'Line of Credit',
  },
];

const STEP_LABELS = {
  'finance-type': 'Finance Type',
  'business-purpose': 'Business Funding Purpose',
  'loan-details': 'Loan Details',
  profile: 'Your Details',
  verify: 'Verify & Submit',
};

const INITIAL_FORM = {
  full_name: '',
  email: '',
  phone: '',
  residential_status: '',
  monthly_income: '',
  monthly_expenses: '',
  asset_type: '',
  loan_purpose: '',
  asset_description: '',
  amount_required: '',
  purchase_price: '',
  deposit: '',
  term_months: '',
  repayment_frequency: '',
  timeframe: '',
  existing_home_loan_monthly: '',
  existing_car_loan_monthly: '',
  existing_personal_loan_monthly: '',
  existing_business_loan_monthly: '',
  existing_credit_card_limit: '',
  business_name: '',
  entity_name: '',
  abn: '',
  years_trading: '',
  industry: '',
  additional_info: '',
};

const LOCKED_PRESETS = {
  consumer_asset_finance: { pathway: 'car_loan' },
  personal_loans: { pathway: 'personal_loan' },
  commercial_asset_finance: { pathway: 'business_finance', purpose: 'asset_equipment_finance' },
  business_loans: { pathway: 'business_finance', purpose: 'business_loan' },
};

function normalizeJourney(value) {
  if (!value) return '';
  const cleaned = String(value).toLowerCase().trim();
  if (['car_loan', 'car-loan', 'car loan'].includes(cleaned)) return 'car_loan';
  if (['personal_loan', 'personal-loan', 'personal loan'].includes(cleaned)) return 'personal_loan';
  if (['business_finance', 'business-finance', 'business finance'].includes(cleaned)) return 'business_finance';
  if (['commercial_car_loan', 'commercial-car-loan', 'commercial car loan'].includes(cleaned)) return 'commercial_car_loan';
  return '';
}

function normalizeCategory(value) {
  if (!value) return '';
  const cleaned = String(value).toLowerCase().trim();
  if (['consumer_asset_finance', 'consumer-asset-finance', 'consumer asset finance'].includes(cleaned)) return 'consumer_asset_finance';
  if (['commercial_asset_finance', 'commercial-asset-finance', 'commercial asset finance'].includes(cleaned)) return 'commercial_asset_finance';
  if (['personal_loans', 'personal-loans', 'personal loans'].includes(cleaned)) return 'personal_loans';
  if (['business_loans', 'business-loans', 'business loans'].includes(cleaned)) return 'business_loans';
  return '';
}

function getJourneyMeta(pathway, businessPurpose) {
  if (pathway === 'car_loan') {
    return {
      title: 'Car Loan',
      applicationCategory: 'consumer_asset_finance',
      financeType: 'Car Loan',
      isBusiness: false,
    };
  }
  if (pathway === 'personal_loan') {
    return {
      title: 'Personal Loan',
      applicationCategory: 'personal_loans',
      financeType: 'Personal Loan',
      isBusiness: false,
    };
  }

  if (pathway === 'commercial_car_loan') {
    return {
      title: 'Commercial Car Loan',
      applicationCategory: 'commercial_asset_finance',
      financeType: 'Commercial Car Loan',
      isBusiness: true,
    };
  }

  const selectedBusiness = BUSINESS_FUNDING_OPTIONS.find((item) => item.value === businessPurpose) || BUSINESS_FUNDING_OPTIONS[1];
  return {
    title: selectedBusiness.title,
    applicationCategory: selectedBusiness.applicationCategory,
    financeType: selectedBusiness.financeType,
    isBusiness: true,
  };
}

export default function ApplyQuick({ defaultJourney = '' }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryCategory = searchParams.get('category');
  const queryJourney = searchParams.get('journey');
  const lockedCategory = normalizeCategory(defaultJourney || queryCategory);
  const lockedPreset = LOCKED_PRESETS[lockedCategory] || null;

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [selectedPathway, setSelectedPathway] = useState(lockedPreset?.pathway || normalizeJourney(queryJourney) || '');
  const [selectedBusinessPurpose, setSelectedBusinessPurpose] = useState(lockedPreset?.purpose || '');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [otpCode, setOtpCode] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationToken, setEmailVerificationToken] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const [applySessionId] = useState(() => {
    if (typeof window === 'undefined') return '';
    const existing = sessionStorage.getItem(APPLY_SESSION_STORAGE_KEY);
    if (existing) return existing;
    const generated = window.crypto?.randomUUID?.() || `apply-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(APPLY_SESSION_STORAGE_KEY, generated);
    return generated;
  });

  const isBusinessPathway = selectedPathway === 'business_finance';

  const stepKeys = useMemo(
    () => (isBusinessPathway ? ['finance-type', 'business-purpose', 'loan-details', 'profile', 'verify'] : ['finance-type', 'loan-details', 'profile', 'verify']),
    [isBusinessPathway]
  );

  const activeStep = stepKeys[currentStepIndex] || stepKeys[0];
  const journey = useMemo(() => getJourneyMeta(selectedPathway, selectedBusinessPurpose), [selectedPathway, selectedBusinessPurpose]);

  const lastViewedStepRef = useRef('');
  const completionTrackedRef = useRef(false);
  const abandonmentTrackedRef = useRef(false);
  const startedTrackedRef = useRef(false);
  const latestStateRef = useRef({
    stepKey: activeStep,
    stepIndex: currentStepIndex,
    route: `${location.pathname}${location.search}`,
    stepCount: stepKeys.length,
    journeyType: selectedPathway || null,
    isBusinessPathway,
  });

  const emitFunnelEvent = ({
    stepAction,
    stepKey,
    stepIndex,
    fromStep,
    toStep,
    fromStepIndex,
    toStepIndex,
    trigger = null,
    label = '',
  }) => {
    if (!applySessionId) return;
    sendConversionEvent({
      event_type: 'apply_funnel',
      event_name: 'apply_step_funnel',
      route: `${location.pathname}${location.search}`,
      destination: '',
      label,
      context: {
        session_id: applySessionId,
        step_action: stepAction,
        step_key: stepKey,
        step_index: stepIndex,
        from_step: fromStep || null,
        to_step: toStep || null,
        from_step_index: fromStepIndex ?? null,
        to_step_index: toStepIndex ?? null,
        total_steps: stepKeys.length,
        journey_type: selectedPathway || null,
        is_business_pathway: isBusinessPathway,
        trigger,
      },
    });
  };

  useEffect(() => {
    latestStateRef.current = {
      stepKey: activeStep,
      stepIndex: currentStepIndex,
      route: `${location.pathname}${location.search}`,
      stepCount: stepKeys.length,
      journeyType: selectedPathway || null,
      isBusinessPathway,
    };
  }, [activeStep, currentStepIndex, location.pathname, location.search, selectedPathway, isBusinessPathway, stepKeys.length]);

  useEffect(() => {
    if (!applySessionId || startedTrackedRef.current) return;
    startedTrackedRef.current = true;
    emitFunnelEvent({
      stepAction: 'start',
      stepKey: activeStep,
      stepIndex: currentStepIndex,
      label: 'apply_flow_started',
    });
  }, [applySessionId]);

  useEffect(() => {
    if (!applySessionId) return;
    const signature = `${activeStep}:${currentStepIndex}`;
    if (lastViewedStepRef.current === signature) return;
    lastViewedStepRef.current = signature;
    emitFunnelEvent({
      stepAction: 'view',
      stepKey: activeStep,
      stepIndex: currentStepIndex,
      label: `view_${activeStep}`,
    });
  }, [applySessionId, activeStep, currentStepIndex]);

  useEffect(() => {
    if (!applySessionId) return undefined;

    const emitAbandon = (trigger) => {
      if (completionTrackedRef.current || abandonmentTrackedRef.current) return;
      const latest = latestStateRef.current;
      abandonmentTrackedRef.current = true;
      sendConversionEvent({
        event_type: 'apply_funnel',
        event_name: 'apply_step_funnel',
        route: latest.route,
        destination: '',
        label: 'apply_flow_abandon',
        context: {
          session_id: applySessionId,
          step_action: 'abandon',
          step_key: latest.stepKey,
          step_index: latest.stepIndex,
          from_step: latest.stepKey,
          to_step: null,
          from_step_index: latest.stepIndex,
          to_step_index: null,
          total_steps: latest.stepCount,
          journey_type: latest.journeyType,
          is_business_pathway: latest.isBusinessPathway,
          trigger,
        },
      });
    };

    const onPageHide = () => emitAbandon('pagehide');
    const onBeforeUnload = () => emitAbandon('beforeunload');
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
      emitAbandon('unmount');
    };
  }, [applySessionId]);

  useEffect(() => {
    if (!isBusinessPathway) {
      setSelectedBusinessPurpose('');
    }
  }, [isBusinessPathway]);

  useEffect(() => {
    if (currentStepIndex > stepKeys.length - 1) {
      setCurrentStepIndex(stepKeys.length - 1);
    }
  }, [currentStepIndex, stepKeys]);

  useEffect(() => {
    setEmailOtpSent(false);
    setEmailVerified(false);
    setOtpCode('');
    setEmailVerificationToken('');
  }, [formData.email]);

  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const validateStep = (stepKey) => {
    if (stepKey === 'finance-type' && !selectedPathway) {
      return { valid: false, message: 'Please choose a finance type to continue.' };
    }

    if (stepKey === 'business-purpose' && isBusinessPathway && !selectedBusinessPurpose) {
      return { valid: false, message: 'Please choose what your business needs funding for.' };
    }

    if (stepKey === 'loan-details') {
      if (!String(formData.amount_required || '').trim() || !String(formData.timeframe || '').trim() || !String(formData.loan_purpose || '').trim()) {
        return { valid: false, message: 'Please complete amount, timeframe, and loan purpose.' };
      }
    }

    if (stepKey === 'profile') {
      const requiredProfile = [formData.full_name, formData.email, formData.phone, formData.monthly_income, formData.monthly_expenses];
      if (!requiredProfile.every((value) => String(value || '').trim().length > 0)) {
        return { valid: false, message: 'Please complete all required personal details.' };
      }
      if (!EMAIL_REGEX.test(formData.email.trim())) {
        return { valid: false, message: 'Please enter a valid email address.' };
      }
      if (!PHONE_REGEX.test(formData.phone.replace(/[\s\-()]+/g, ''))) {
        return { valid: false, message: 'Please enter a valid phone number.' };
      }
      if (journey.isBusiness) {
        const businessRequired = [formData.business_name, formData.entity_name, formData.abn, formData.years_trading, formData.industry];
        if (!businessRequired.every((value) => String(value || '').trim().length > 0)) {
          return { valid: false, message: 'Please complete all required business details.' };
        }
      }
    }

    if (stepKey === 'verify' && (!emailVerified || !emailVerificationToken)) {
      return { valid: false, message: 'Please verify your email before submitting.' };
    }

    return { valid: true, message: '' };
  };

  const handleNext = () => {
    const result = validateStep(activeStep);
    if (!result.valid) {
      toast.error(result.message);
      return;
    }
    const nextStepIndex = Math.min(currentStepIndex + 1, stepKeys.length - 1);
    const nextStep = stepKeys[nextStepIndex] || activeStep;
    emitFunnelEvent({
      stepAction: 'advance',
      stepKey: activeStep,
      stepIndex: currentStepIndex,
      fromStep: activeStep,
      toStep: nextStep,
      fromStepIndex: currentStepIndex,
      toStepIndex: nextStepIndex,
      label: `${activeStep}_to_${nextStep}`,
    });
    setCurrentStepIndex(nextStepIndex);
  };

  const handleBack = () => {
    const prevStepIndex = Math.max(currentStepIndex - 1, 0);
    const prevStep = stepKeys[prevStepIndex] || activeStep;
    emitFunnelEvent({
      stepAction: 'back',
      stepKey: activeStep,
      stepIndex: currentStepIndex,
      fromStep: activeStep,
      toStep: prevStep,
      fromStepIndex: currentStepIndex,
      toStepIndex: prevStepIndex,
      label: `${activeStep}_back_to_${prevStep}`,
    });
    setCurrentStepIndex(prevStepIndex);
  };

  const sendEmailOtp = async () => {
    if (!EMAIL_REGEX.test(formData.email.trim())) {
      toast.error('Please enter a valid email before requesting verification.');
      return;
    }
    setIsSendingOtp(true);
    try {
      await axios.post(`${API}/application/email-otp/send`, { email: formData.email.trim() });
      setEmailOtpSent(true);
      toast.success('Verification code sent to your email.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send verification code.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (!otpCode.trim()) {
      toast.error('Please enter the verification code.');
      return;
    }
    setIsVerifyingOtp(true);
    try {
      const response = await axios.post(`${API}/application/email-otp/verify`, {
        email: formData.email.trim(),
        code: otpCode.trim(),
      });
      setEmailVerified(true);
      setEmailVerificationToken(response.data?.verification_token || '');
      toast.success('Email verified successfully.');
    } catch (error) {
      setEmailVerified(false);
      setEmailVerificationToken('');
      toast.error(error?.response?.data?.detail || 'Email verification failed.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    for (const stepKey of stepKeys) {
      const result = validateStep(stepKey);
      if (!result.valid) {
        toast.error(result.message);
        setCurrentStepIndex(stepKeys.indexOf(stepKey));
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const existingLoans = {
        home_loan_monthly: formData.existing_home_loan_monthly || '0',
        car_loan_monthly: formData.existing_car_loan_monthly || '0',
        personal_loan_monthly: formData.existing_personal_loan_monthly || '0',
        business_loan_monthly: formData.existing_business_loan_monthly || '0',
        credit_card_limit: formData.existing_credit_card_limit || '0',
      };

      const existingFinanceSummary = `Home loan: ${existingLoans.home_loan_monthly}, Car loan: ${existingLoans.car_loan_monthly}, Personal loan: ${existingLoans.personal_loan_monthly}, Business loan: ${existingLoans.business_loan_monthly}, Credit card limit: ${existingLoans.credit_card_limit}`;

      const payload = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        business_name: journey.isBusiness ? formData.business_name.trim() : `${formData.full_name.trim()} Applicant`,
        abn: journey.isBusiness ? formData.abn.trim() : 'N/A',
        years_trading: journey.isBusiness ? formData.years_trading : 'N/A',
        industry: journey.isBusiness ? formData.industry : 'Consumer',
        finance_type: journey.financeType,
        application_category: journey.applicationCategory,
        amount_required: formData.amount_required.trim(),
        asset_description: formData.asset_description.trim() || formData.loan_purpose.trim(),
        loan_purpose: formData.loan_purpose.trim(),
        timeframe: formData.timeframe,
        existing_finance: existingFinanceSummary,
        additional_info: formData.additional_info || null,
        email_verification_token: emailVerificationToken,
        existing_loans: existingLoans,
        journey_type: selectedPathway,
        business_funding_purpose: selectedBusinessPurpose || null,
        ...formData,
      };

      await axios.post(`${API}/application`, payload);
      completionTrackedRef.current = true;
      setIsSubmitted(true);
      emitFunnelEvent({
        stepAction: 'completed',
        stepKey: activeStep,
        stepIndex: currentStepIndex,
        fromStep: activeStep,
        toStep: 'submitted',
        fromStepIndex: currentStepIndex,
        toStepIndex: stepKeys.length,
        label: 'application_submitted',
      });
      toast.success('Application submitted successfully.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4">
        <SEO title="Application Received | Flash Funding" description="Thank you for your finance application." canonical="/apply" />
        <section className="max-w-2xl mx-auto rounded-2xl border border-purple-500/30 bg-navy-800/80 p-8 sm:p-10 text-center" data-testid="apply-success-card">
          <div className="w-20 h-20 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-8" data-testid="apply-success-icon-wrap">
            <CheckCircle size={40} className="text-purple-300" data-testid="apply-success-icon" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl text-white tracking-tight mb-6" data-testid="apply-success-heading">Application received.</h1>
          <p className="font-body text-base sm:text-lg text-white/75 mb-4" data-testid="apply-success-message">Thanks for submitting your details. Our team will review your file and contact you shortly.</p>
          <div className="bg-navy-900 border border-purple-500/20 p-6 rounded-xl mb-8" data-testid="apply-success-note">
            <div className="flex items-center gap-3 justify-center text-purple-300" data-testid="apply-success-note-content">
              <Shield size={20} />
              <span className="font-body text-sm">No credit check has been performed at this stage.</span>
            </div>
          </div>
          <p className="font-body text-white/60 mb-8" data-testid="apply-success-phone-text">
            Need to speak to someone urgently? Call us on{' '}
            <a href="tel:+611800003527" className="text-purple-300 hover:underline font-mono" data-testid="apply-success-phone-link">1800 003 527</a>
          </p>
          <Link to="/" data-testid="apply-success-home-link">
            <Button className="rounded-full gold-gradient text-white font-heading font-bold uppercase tracking-[0.14em] text-xs px-7 py-6" data-testid="apply-success-home-button">Return to Home</Button>
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="apply-page">
      <SEO
        title="Start Application | Flash Funding"
        description="Guided application flow for car loans, personal loans, and business finance."
        keywords="car loan application, personal loan application, business finance application"
        canonical="/apply"
      />

      <section className="max-w-5xl mx-auto" data-testid="apply-stepper-shell">
        <div className="rounded-2xl border border-purple-500/25 bg-navy-800/85 backdrop-blur-xl p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-7">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-purple-300" data-testid="apply-page-badge">Guided Application</p>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-1" data-testid="apply-main-heading">Check Your Options</h1>
            </div>
            <p className="text-sm text-white/60" data-testid="apply-step-counter">Step {currentStepIndex + 1} of {stepKeys.length}</p>
          </div>

          {lockedPreset && (
            <div className="rounded-xl border border-purple-500/25 bg-navy-900/70 p-4 mb-6" data-testid="apply-locked-journey-note">
              <p className="text-white/85 text-sm">This page is pre-set for a specific finance path. You can continue with the guided steps below.</p>
            </div>
          )}

          <ol className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-8" data-testid="apply-stepper-progress">
            {stepKeys.map((key, index) => {
              const isActive = index === currentStepIndex;
              const isComplete = index < currentStepIndex;
              return (
                <li key={key} className={`rounded-lg border px-3 py-2 text-xs ${isActive ? 'border-purple-400 bg-purple-500/15 text-white' : isComplete ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200' : 'border-white/10 text-white/65'}`} data-testid={`apply-stepper-item-${key}`}>
                  <span className="font-semibold">{index + 1}. </span>{STEP_LABELS[key]}
                </li>
              );
            })}
          </ol>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="apply-stepper-form">
            {activeStep === 'finance-type' && (
              <div className="space-y-4" data-testid="apply-step-finance-type">
                <h2 className="text-xl font-semibold text-white" data-testid="apply-finance-type-question">What type of finance do you need?</h2>
                <div className="grid md:grid-cols-3 gap-3" data-testid="apply-finance-type-options-grid">
                  {JOURNEY_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      disabled={!!lockedPreset}
                      onClick={() => {
                        setSelectedPathway(option.value);
                        setCurrentStepIndex(0);
                      }}
                      data-testid={`apply-finance-type-${option.value}`}
                      className={`rounded-xl border p-4 text-left transition-[border-color,background-color] ${selectedPathway === option.value ? 'border-purple-400 bg-purple-500/15' : 'border-purple-500/20 bg-navy-900/80 hover:border-purple-500/45'} ${lockedPreset ? 'opacity-80 cursor-not-allowed' : ''}`}
                    >
                      <option.icon size={18} className="text-purple-300 mb-2" />
                      <p className="text-white font-semibold">{option.title}</p>
                      <p className="text-sm text-white/60">{option.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeStep === 'business-purpose' && (
              <div className="space-y-4" data-testid="apply-step-business-purpose">
                <h2 className="text-xl font-semibold text-white" data-testid="apply-business-purpose-question">What do you need funding for?</h2>
                <div className="grid md:grid-cols-2 gap-3" data-testid="apply-business-purpose-options-grid">
                  {BUSINESS_FUNDING_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSelectedBusinessPurpose(option.value)}
                      data-testid={`apply-business-purpose-${option.value}`}
                      className={`rounded-xl border p-4 text-left transition-[border-color,background-color] ${selectedBusinessPurpose === option.value ? 'border-purple-400 bg-purple-500/15' : 'border-purple-500/20 bg-navy-900/80 hover:border-purple-500/45'}`}
                    >
                      <p className="text-white font-semibold">{option.title}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeStep === 'loan-details' && (
              <div className="rounded-xl border border-purple-500/20 bg-navy-900/75 p-5" data-testid="apply-step-loan-details">
                <h2 className="text-lg font-semibold text-white mb-4">Loan Details</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80" htmlFor="apply-amount-required">Amount Required*</Label>
                    <Input id="apply-amount-required" value={formData.amount_required} onChange={(e) => setField('amount_required', e.target.value)} className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-amount-required" />
                  </div>
                  <div>
                    <Label className="text-white/80">When Needed*</Label>
                    <Select value={formData.timeframe} onValueChange={(value) => setField('timeframe', value)}>
                      <SelectTrigger className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-timeframe"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-navy-900 border-purple-500/20 text-white">
                        {['ASAP', '1-2 weeks', '1 month', '2-3 months', 'Just researching'].map((option) => (
                          <SelectItem key={option} value={option} className="text-white focus:bg-purple-500/20" data-testid={`apply-option-timeframe-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/80">Asset / Loan Type</Label>
                    <Select value={formData.asset_type} onValueChange={(value) => setField('asset_type', value)}>
                      <SelectTrigger className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-asset-type"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-navy-900 border-purple-500/20 text-white">
                        {['Passenger Vehicle', 'Ute / 4WD', 'Truck', 'Plant & Equipment', 'Debt Consolidation', 'Working Capital', 'Business Expansion', 'Line of Credit', 'Other'].map((option) => (
                          <SelectItem key={option} value={option} className="text-white focus:bg-purple-500/20" data-testid={`apply-option-asset-type-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/80" htmlFor="apply-term-months">Term (Months)</Label>
                    <Input id="apply-term-months" value={formData.term_months} onChange={(e) => setField('term_months', e.target.value)} className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-term-months" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-white/80" htmlFor="apply-loan-purpose">Loan Purpose / Asset Details*</Label>
                    <Textarea id="apply-loan-purpose" value={formData.loan_purpose} onChange={(e) => setField('loan_purpose', e.target.value)} rows={3} className="mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-loan-purpose" />
                  </div>
                </div>
              </div>
            )}

            {activeStep === 'profile' && (
              <div className="space-y-5" data-testid="apply-step-profile">
                <div className="rounded-xl border border-purple-500/20 bg-navy-900/75 p-5" data-testid="apply-contact-section">
                  <h2 className="text-lg font-semibold text-white mb-4">Contact Details</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/80" htmlFor="apply-full-name">Full Name*</Label>
                      <Input id="apply-full-name" value={formData.full_name} onChange={(e) => setField('full_name', e.target.value)} className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-full-name" />
                    </div>
                    <div>
                      <Label className="text-white/80" htmlFor="apply-email">Email*</Label>
                      <Input id="apply-email" type="email" value={formData.email} onChange={(e) => setField('email', e.target.value)} className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-email" />
                    </div>
                    <div>
                      <Label className="text-white/80" htmlFor="apply-phone">Phone*</Label>
                      <Input id="apply-phone" value={formData.phone} onChange={(e) => setField('phone', e.target.value)} className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-phone" />
                    </div>
                    <div>
                      <Label className="text-white/80">Residential Status</Label>
                      <Select value={formData.residential_status} onValueChange={(value) => setField('residential_status', value)}>
                        <SelectTrigger className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-residential-status"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="bg-navy-900 border-purple-500/20 text-white">
                          {['Owner with mortgage', 'Owner outright', 'Renting', 'Living with family'].map((option) => (
                            <SelectItem key={option} value={option} className="text-white focus:bg-purple-500/20" data-testid={`apply-option-residential-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-purple-500/20 bg-navy-900/75 p-5" data-testid="apply-financial-section">
                  <h2 className="text-lg font-semibold text-white mb-4">Income and Expense Snapshot</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/80" htmlFor="apply-monthly-income">Monthly Income (After Tax)*</Label>
                      <Input id="apply-monthly-income" value={formData.monthly_income} onChange={(e) => setField('monthly_income', e.target.value)} className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-monthly-income" />
                    </div>
                    <div>
                      <Label className="text-white/80" htmlFor="apply-monthly-expenses">Monthly Living Expenses*</Label>
                      <Input id="apply-monthly-expenses" value={formData.monthly_expenses} onChange={(e) => setField('monthly_expenses', e.target.value)} className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-monthly-expenses" />
                    </div>
                  </div>
                </div>

                {journey.isBusiness && (
                  <div className="rounded-xl border border-purple-500/20 bg-navy-900/75 p-5" data-testid="apply-business-section">
                    <h2 className="text-lg font-semibold text-white mb-4">Business Details</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/80" htmlFor="apply-business-name">Business Name*</Label>
                        <Input id="apply-business-name" value={formData.business_name} onChange={(e) => setField('business_name', e.target.value)} className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-business-name" />
                      </div>
                      <div>
                        <Label className="text-white/80" htmlFor="apply-entity-name">Entity Name*</Label>
                        <Input id="apply-entity-name" value={formData.entity_name} onChange={(e) => setField('entity_name', e.target.value)} className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-entity-name" />
                      </div>
                      <div>
                        <Label className="text-white/80" htmlFor="apply-abn">ABN*</Label>
                        <Input id="apply-abn" value={formData.abn} onChange={(e) => setField('abn', e.target.value)} className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-abn" />
                      </div>
                      <div>
                        <Label className="text-white/80">Years Trading*</Label>
                        <Select value={formData.years_trading} onValueChange={(value) => setField('years_trading', value)}>
                          <SelectTrigger className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-years-trading"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent className="bg-navy-900 border-purple-500/20 text-white">
                            {['0-1', '1-2', '2-5', '5-10', '10+'].map((option) => (
                              <SelectItem key={option} value={option} className="text-white focus:bg-purple-500/20" data-testid={`apply-option-years-${option.replace(/[^a-z0-9]+/g, '-')}`}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-white/80">Industry*</Label>
                        <Select value={formData.industry} onValueChange={(value) => setField('industry', value)}>
                          <SelectTrigger className="h-11 mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-industry"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent className="bg-navy-900 border-purple-500/20 text-white">
                            {['Construction', 'Transport & Logistics', 'Mining', 'Agriculture', 'Manufacturing', 'Healthcare', 'Retail', 'Trades & Services', 'Professional Services', 'Other'].map((option) => (
                              <SelectItem key={option} value={option} className="text-white focus:bg-purple-500/20" data-testid={`apply-option-industry-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeStep === 'verify' && (
              <div className="space-y-5" data-testid="apply-step-verify">
                <div className="rounded-xl border border-purple-500/20 bg-navy-900/75 p-5" data-testid="apply-summary-card">
                  <h2 className="text-lg font-semibold text-white mb-3">Application Summary</h2>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <p className="text-white/75" data-testid="apply-summary-pathway"><span className="text-white">Pathway:</span> {journey.isBusiness ? 'Business Finance' : journey.title}</p>
                    <p className="text-white/75" data-testid="apply-summary-product"><span className="text-white">Product:</span> {journey.title}</p>
                    <p className="text-white/75" data-testid="apply-summary-amount"><span className="text-white">Amount:</span> {formData.amount_required || 'Not set'}</p>
                    <p className="text-white/75" data-testid="apply-summary-timeframe"><span className="text-white">Timeframe:</span> {formData.timeframe || 'Not set'}</p>
                  </div>
                </div>

                <div className="rounded-xl border border-purple-500/20 bg-navy-900/75 p-5" data-testid="apply-otp-section">
                  <h2 className="text-lg font-semibold text-white mb-3">Verify Email</h2>
                  <p className="text-sm text-white/70 mb-4" data-testid="apply-email-otp-note">We send a one-time code to authenticate your email before submission.</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button type="button" onClick={sendEmailOtp} disabled={isSendingOtp} className="rounded-full bg-purple-600 hover:bg-purple-500 text-white" data-testid="apply-send-email-otp-button">
                      {isSendingOtp ? 'Sending...' : (emailOtpSent ? 'Resend code' : 'Send code')}
                    </Button>
                    <Input value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="Enter 6-digit code" className="h-11 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-email-otp-input" />
                    <Button type="button" onClick={verifyEmailOtp} disabled={isVerifyingOtp} className="rounded-full bg-purple-700 hover:bg-purple-600 text-white" data-testid="apply-verify-email-otp-button">
                      {isVerifyingOtp ? 'Verifying...' : 'Verify code'}
                    </Button>
                  </div>
                  {emailVerified && (
                    <div className="inline-flex items-center gap-2 text-emerald-300 text-sm mt-3" data-testid="apply-email-verified-indicator">
                      <MailCheck size={16} /> Email verified
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-purple-500/20 bg-navy-900/75 p-5" data-testid="apply-notes-section">
                  <Label className="text-white/80" htmlFor="apply-additional-info">Additional Information</Label>
                  <Textarea id="apply-additional-info" value={formData.additional_info} onChange={(e) => setField('additional_info', e.target.value)} rows={3} className="mt-2 bg-navy-800 border-purple-500/20 text-white" data-testid="apply-field-additional-info" />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-2" data-testid="apply-stepper-navigation">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={currentStepIndex === 0}
                className="rounded-full border-purple-400/30 bg-navy-900/70 text-white hover:bg-purple-500/15"
                data-testid="apply-step-back-button"
              >
                <ArrowLeft size={15} className="mr-1" /> Back
              </Button>

              {activeStep !== 'verify' ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  className="rounded-full bg-purple-600 hover:bg-purple-500 text-white font-semibold"
                  data-testid="apply-step-next-button"
                >
                  Continue <ArrowRight size={15} className="ml-1" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-full gold-gradient text-white font-heading font-bold text-xs uppercase tracking-[0.14em] px-7 py-6"
                  data-testid="apply-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'See What You Can Get Approved For'}
                </Button>
              )}
            </div>
          </form>

          <div className="mt-8 rounded-xl border border-purple-500/20 bg-navy-900/65 p-4" data-testid="apply-no-credit-check-note">
            <p className="text-xs text-white/70 flex items-center gap-2" data-testid="apply-no-credit-check-text">
              <Shield size={14} className="text-purple-300" />
              Email verification and phone validation are completed before submission.
            </p>
          </div>

          <div className="mt-8 text-center" data-testid="apply-phone-support-wrap">
            <p className="font-body text-white/60 mb-3" data-testid="apply-contact-alt-text">Prefer to speak to someone directly?</p>
            <a href="tel:+611800003527" className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors" data-testid="apply-contact-alt-link">
              <Phone size={18} />
              <span className="font-mono text-lg" data-testid="apply-contact-alt-number">1800 003 527</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
